import React, { useEffect, useState } from 'react'
import {
  ApolloQueryResult,
  gql,
  useApolloClient,
  useMutation,
} from '@apollo/client'
import Head from 'next/head'
import { useRouter } from 'next/router'
import ArticleContent from 'components/article/index'
import Schema from 'components/article/schema'
import { ArticleJsonLd } from 'components/shared/schema/article'
import {
  ArticleCommentsDocument,
  Article_With_BrokersDocument,
} from 'graphql/generated/types'
import ApplicationLayout from 'layouts/application'
import { parseCookies, setCookie, destroyCookie } from 'nookies'
import { countComments, pageUri } from 'shared/util'
import { addApolloState, initializeApollo } from 'util/apolloClient'

const title = (name) => name

const description = (name) => name

const UPDATE_ARTICLE_VIEWS = gql`
  mutation ($slug: String) {
    createArticleViews(slug: $slug)
  }
`

export default function Article(props) {
  const router = useRouter()
  const {
    query: { slug },
    asPath,
  } = router
  const [ArticleViews] = useMutation(UPDATE_ARTICLE_VIEWS)
  const apolloClient = useApolloClient()
  const { data: propsData } = props
  const [data, setData] = useState(propsData)
  const {
    all_brokers: allBrokers,
    article,
    nav_calculators: navCalculators,
    all_pages: allPages,
  } = data
  const articleData = article.data[0]
  // mutation to update article views
  const { relatedArticles } = articleData.attributes
  const {
    title: dataTitle,
    description: dataDescription,
    seo_meta: seoMeta,
    image,
    country,
    user,
  } = articleData.attributes
  useEffect(() => {
    ArticleViews({
      variables: {
        slug,
      },
    })

    apolloClient
      .query({
        query: ArticleCommentsDocument,
        variables: {
          relation: `api::article.article:${data.article.data[0].id}`,
          sort: 'createdAt:desc',
        },
      })
      .then((res: ApolloQueryResult<any>) => {
        if (
          countComments(res.data.plugin_comments) !==
          countComments(data.article.data[0].attributes.plugin_comments)
        ) {
          data.article.data[0].attributes.plugin_comments =
            res.data.plugin_comments
          setData(data)
        }
      })

    // handlePageLoad(apolloClient)
    const mapFrontendCountrySlug = (countryVal) => {
      switch (countryVal) {
        case 'usa':
          return 'us'
        case 'canada':
          return 'ca'
        case 'india':
          return 'in'
        default:
          return null
      }
    }
    const cookies = parseCookies()
    if (!cookies.country) {
      setCookie(
        null,
        'country',
        mapFrontendCountrySlug(country.data.attributes.name.toLowerCase()),
        {
          path: ['/'], // cookie path
        }
      )
      destroyCookie(null, 'country', {
        path: '/article',
        expires: new Date(0),
      })
    }
  }, [])

  // The following reset is required for SPA navigation but it causes errors. It is commented out for now.
  // useEffect(() => {
  //   return () => {
  //     // unmount optinmonster
  //     try {
  //       window.om107067_95746?.reset()
  //     } catch (e) {
  //       // do nothing
  //     }
  //   }
  // })

  const { title: recordTitle, description: recordDescription } = seoMeta || {}
  const pageTitle = title(dataTitle)
  const pageDescription = description(dataDescription)

  let images = []

  if (image?.file?.data) {
    const availableImage =
      image.file.data[0]?.attributes.formats.large ||
      image.file?.data[0]?.attributes.url
    images = [availableImage]
  }

  return (
    <ApplicationLayout
      allBrokers={allBrokers.data}
      navCalculators={navCalculators.data}
      country={country.data}
      allPages={allPages.data}
    >
      <Head>
        <title>{recordTitle || pageTitle}</title>
        <Schema articleData={articleData} />
        <meta
          name="description of the article"
          content={recordDescription || pageDescription}
        />
      </Head>
      <ArticleJsonLd
        url={pageUri(asPath)}
        images={images}
        title={pageTitle}
        description={pageDescription}
        authorName={user.data?.attributes?.username || 'Fintrakk Admin'}
      />
      <ArticleContent articleData={articleData} articleList={relatedArticles} />
    </ApplicationLayout>
  )
}

export async function getStaticProps({ params }) {
  const apolloClient = initializeApollo()
  const { slug } = params
  const data = await apolloClient.query({
    query: Article_With_BrokersDocument,
    variables: {
      slug,
    },
  })
  if (data.data.article.data.length === 0) {
    return {
      notFound: true,
    }
  }

  return addApolloState(apolloClient, {
    props: data,
    revalidate: 86400,
  })
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {
  // const apolloClient = initializeApollo()

  // const {
  //   data: {
  //     articlesConnection: {
  //       aggregate: { count: articlesCount }
  //     }
  //   }
  // } = await apolloClient.query({
  //   query: GET_ARTICLES_COUNT
  // })

  // Get the paths we want to pre-render based on posts
  // const paths = projectsList.map(project => ({
  //   params: { project_slug: project.attributes.slug }
  // }))

  const paths = [
    {
      params: {
        slug: 'which-is-the-best-time-frame-for-options-trading',
      },
    },
  ]

  return { paths, fallback: 'blocking' }
}
